import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, IconButton, CircularProgress,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CampaignIcon from '@mui/icons-material/Campaign';
import { IListningarEntry, IListningarWeekGroup, IListningarCategory } from './types';
import { fetchEntries, deleteEntry, updateEntry, fetchCategories } from './listningarApi';
import ListningarFilterBar from './ListningarFilterBar';
import ListningarWeekSection from './ListningarWeekSection';
import ListningarCategoryManager from './ListningarCategoryManager';
import { hasPermission } from '../../utils/permissionUtils';

interface ListningarTimelineProps {
  onClose: () => void;
  onCreateNew: () => void;
  onEdit: (entry: IListningarEntry) => void;
  currentUser: any;
  refreshKey: number;
}

function groupEntriesByWeek(entries: IListningarEntry[]): IListningarWeekGroup[] {
  const groupMap = new Map<string, IListningarWeekGroup>();

  const pinnedEntries = entries.filter(e => e.isPinned);
  const regularEntries = entries.filter(e => !e.isPinned);

  for (const entry of regularEntries) {
    const key = `${entry.year}-${entry.weekNumber}`;
    if (!groupMap.has(key)) {
      groupMap.set(key, {
        weekNumber: entry.weekNumber,
        year: entry.year,
        label: `Vecka ${entry.weekNumber}, ${entry.year}`,
        entries: [],
        highlight: undefined,
      });
    }
    const group = groupMap.get(key)!;
    group.entries.push(entry);
    if (entry.isHighlight) {
      group.highlight = entry;
    }
  }

  const groups = Array.from(groupMap.values());
  groups.sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.weekNumber - a.weekNumber;
  });

  if (pinnedEntries.length > 0) {
    groups.unshift({
      weekNumber: 0,
      year: 0,
      label: 'Fästa inlägg',
      entries: pinnedEntries,
      highlight: pinnedEntries.find(e => e.isHighlight),
    });
  }

  return groups;
}

const LAST_VISIT_KEY = 'listningar_last_visit';

const ListningarTimeline: React.FC<ListningarTimelineProps> = ({
  onClose,
  onCreateNew,
  onEdit,
  currentUser,
  refreshKey,
}) => {
  const [entries, setEntries] = useState<IListningarEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [deleteDialogEntry, setDeleteDialogEntry] = useState<IListningarEntry | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [lastVisitTimestamp, setLastVisitTimestamp] = useState<string | undefined>(undefined);
  const [categories, setCategories] = useState<IListningarCategory[]>([]);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [cardSize, setCardSize] = useState<'landscape' | 'square' | 'portrait'>('portrait');
  const [selectedWeek, setSelectedWeek] = useState<string>('all');

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const LIMIT = 50;
  const canCreateEntries = hasPermission(currentUser, 'listningar:create');
  const canEditEntries = hasPermission(currentUser, 'listningar:update');
  const canDeleteEntries = hasPermission(currentUser, 'listningar:delete');
  const canFeatureEntries = canEditEntries;
  const canManageCategories = canEditEntries || canDeleteEntries;

  useEffect(() => {
    fetchCategories().then(setCategories).catch(err => console.error('Failed to load categories:', err));
  }, [refreshKey]);

  useEffect(() => {
    const lastVisit = localStorage.getItem(LAST_VISIT_KEY);
    if (lastVisit) {
      setLastVisitTimestamp(lastVisit);
    }
    localStorage.setItem(LAST_VISIT_KEY, new Date().toISOString());
  }, []);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearchDebounced(searchQuery);
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery]);

  const loadEntries = useCallback(async (reset = false, currentOffset = 0) => {
    try {
      if (reset) {
        setLoading(true);
        setOffset(0);
      } else {
        setLoadingMore(true);
      }
      const data = await fetchEntries({
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        search: searchDebounced || undefined,
        limit: LIMIT,
        offset: currentOffset,
      });

      if (reset) {
        setEntries(data.entries);
      } else {
        setEntries(prev => [...prev, ...data.entries]);
      }
      setTotal(data.total);
      setOffset(currentOffset + data.entries.length);
    } catch (err) {
      console.error('Failed to load entries:', err);
      setSnackbar({ open: true, message: 'Kunde inte hämta inlägg', severity: 'error' });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [selectedCategory, searchDebounced]);

  useEffect(() => {
    loadEntries(true, 0);
  }, [refreshKey, loadEntries]);

  const hasMore = entries.length < total;

  const handleDelete = async () => {
    if (!deleteDialogEntry) return;
    try {
      await deleteEntry(deleteDialogEntry._id);
      setEntries(prev => prev.filter(e => e._id !== deleteDialogEntry._id));
      setTotal(prev => prev - 1);
      setSnackbar({ open: true, message: 'Inlägget har tagits bort', severity: 'success' });
    } catch (err) {
      console.error('Failed to delete entry:', err);
      setSnackbar({ open: true, message: 'Kunde inte ta bort inlägget', severity: 'error' });
    } finally {
      setDeleteDialogEntry(null);
    }
  };

  const handleToggleHighlight = async (entry: IListningarEntry) => {
    try {
      const updated = await updateEntry(entry._id, { isHighlight: !entry.isHighlight });
      setEntries(prev => prev.map(e => {
        if (e._id === updated._id) return updated;
        if (updated.isHighlight && e.weekNumber === updated.weekNumber && e.year === updated.year && e._id !== updated._id) {
          return { ...e, isHighlight: false };
        }
        return e;
      }));
    } catch (err) {
      console.error('Failed to toggle highlight:', err);
      setSnackbar({ open: true, message: 'Kunde inte uppdatera highlight', severity: 'error' });
    }
  };

  const handleTogglePin = async (entry: IListningarEntry) => {
    try {
      const updated = await updateEntry(entry._id, { isPinned: !entry.isPinned });
      setEntries(prev => prev.map(e => e._id === updated._id ? updated : e));
    } catch (err) {
      console.error('Failed to toggle pin:', err);
      setSnackbar({ open: true, message: 'Kunde inte uppdatera nål', severity: 'error' });
    }
  };

  const allWeekGroups = groupEntriesByWeek(entries);

  const availableWeeks = allWeekGroups
    .filter(g => g.weekNumber > 0)
    .map(g => ({ value: `${g.year}-${g.weekNumber}`, label: `v${g.weekNumber}, ${g.year}` }));

  const weekGroups = selectedWeek === 'all'
    ? allWeekGroups
    : allWeekGroups.filter(g =>
        g.weekNumber === 0 ||
        `${g.year}-${g.weekNumber}` === selectedWeek
      );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fafafa',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 1.5, sm: 3 },
          py: { xs: 1, sm: 2 },
          bgcolor: '#fff',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CampaignIcon sx={{ color: '#072f53', fontSize: { xs: 22, sm: 28 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#072f53', fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
            Listningar
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5, display: { xs: 'none', sm: 'block' } }}>
            Se innehållet
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {canCreateEntries && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateNew}
              size="small"
              sx={{
                bgcolor: '#072f53',
                '&:hover': { bgcolor: '#0a4175' },
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
            >
              Lägg till
            </Button>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: { xs: 1, sm: 3 },
          py: { xs: 1.5, sm: 3 },
        }}
      >
        {/* <ListningarStatsBar refreshKey={refreshKey} /> */}

        <ListningarFilterBar
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          categories={categories}
          canManageCategories={canManageCategories}
          onManageCategories={canManageCategories ? () => setShowCategoryManager(true) : undefined}
          cardSize={cardSize}
          onCardSizeChange={setCardSize}
          selectedWeek={selectedWeek}
          onWeekChange={setSelectedWeek}
          availableWeeks={availableWeeks}
        />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && entries.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 10,
            }}
          >
            <CampaignIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchDebounced || selectedCategory !== 'all'
                ? 'Inga inlägg matchar din sökning'
                : 'Inga inlägg ännu'
              }
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchDebounced || selectedCategory !== 'all'
                ? 'Prova att ändra filter eller sökord'
                : 'Bli den första att lägga till ett inlägg!'
              }
            </Typography>
            {canCreateEntries && !searchDebounced && selectedCategory === 'all' && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreateNew}
                sx={{
                  bgcolor: '#072f53',
                  '&:hover': { bgcolor: '#0a4175' },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Lägg till första inlägget
              </Button>
            )}
          </Box>
        )}

        {!loading && weekGroups.map(group => (
          <ListningarWeekSection
            key={`${group.year}-${group.weekNumber}`}
            weekGroup={group}
            canEditEntries={canEditEntries}
            canDeleteEntries={canDeleteEntries}
            canFeatureEntries={canFeatureEntries}
            currentUserId={currentUser?._id || ''}
            lastVisitTimestamp={lastVisitTimestamp}
            onEdit={onEdit}
            onDelete={(entry) => setDeleteDialogEntry(entry)}
            onToggleHighlight={handleToggleHighlight}
            onTogglePin={handleTogglePin}
            categories={categories}
            cardSize={cardSize}
          />
        ))}

        {!loading && hasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <Button
              variant="outlined"
              onClick={() => loadEntries(false, offset)}
              disabled={loadingMore}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
              }}
            >
              {loadingMore ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              Ladda fler inlägg
            </Button>
          </Box>
        )}
      </Box>

      <Dialog open={!!deleteDialogEntry} onClose={() => setDeleteDialogEntry(null)}>
        <DialogTitle>Ta bort inlägg?</DialogTitle>
        <DialogContent>
          <Typography>
            Är du säker på att du vill ta bort <strong>"{deleteDialogEntry?.title}"</strong>?
            Detta kan inte ångras.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogEntry(null)}>Avbryt</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Ta bort
          </Button>
        </DialogActions>
      </Dialog>

      {showCategoryManager && canManageCategories && (
        <ListningarCategoryManager
          open={showCategoryManager}
          onClose={() => setShowCategoryManager(false)}
          canUpdateCategories={canEditEntries}
          canDeleteCategories={canDeleteEntries}
          onCategoriesChanged={() => {
            fetchCategories().then(setCategories).catch(() => {});
          }}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ListningarTimeline;
